import { createStore } from 'zustand/vanilla'
import type { ServiceAlert, VehicleCounts, VehiclePosition, VehicleType } from '@traintopia/contracts'
import type { StationInfo, BusStopInfo } from '../components/Map/stationInfo'
import { api } from '../services/api'

export type {
  ServiceAlert,
  VehiclePosition,
  VehicleStatus,
  VehicleType,
} from '@traintopia/contracts'

export type TransitMode = 'subway' | 'bus' | 'ferry' | 'rail' | 'njbus'

/** Every mode, in the one order a filter is stored and labelled in. */
export const TRANSIT_MODES: readonly TransitMode[] = ['subway', 'bus', 'ferry', 'rail', 'njbus']

/**
 * Which fleets the map shows: one or more modes, in `TRANSIT_MODES` order. Never empty — a
 * map with every fleet switched off reads as broken, not as a choice. "All" is not a value
 * of its own; it is every mode selected, so there is exactly one way to spell it.
 */
export type TransitFilter = readonly TransitMode[]

/** Whether the filter admits `mode`. Gates must use this — a POSITIVE test per mode. Takes
 *  a VehicleType so a vehicle's own `type` can be passed; one no mode covers is never shown. */
export function showsMode(filter: TransitFilter, mode: VehicleType): boolean {
  return (filter as readonly VehicleType[]).includes(mode)
}

export function showsAllModes(filter: TransitFilter): boolean {
  return filter.length === TRANSIT_MODES.length
}

/** Canonical order, deduplicated; null if nothing is left (callers keep the old filter). */
function normalizeFilter(modes: Iterable<TransitMode>): TransitFilter | null {
  const set = new Set(modes)
  const out = TRANSIT_MODES.filter((m) => set.has(m))
  return out.length > 0 ? out : null
}

function sameFilter(a: TransitFilter, b: TransitFilter): boolean {
  return a.length === b.length && a.every((m, i) => m === b[i])
}

interface TransitState {
  vehicles: Record<string, VehiclePosition>
  /** Alerts in effect NOW, from the WebSocket. Upcoming planned work is not pushed (the
   *  socket subscription asks for `alerts: 'live'`) — it is `upcomingAlerts`. */
  alerts: ServiceAlert[]
  /**
   * Planned work not yet in effect, loaded over REST by `loadUpcomingAlerts` when the Alerts
   * sheet shows it. It is ~70% of all alerts by size and only that sheet reads it, so pushing
   * it to every client on every connect and every alert change was most of what the socket
   * carried for alerts. Null until first loaded.
   */
  upcomingAlerts: ServiceAlert[] | null
  upcomingAlertsStatus: 'idle' | 'loading' | 'loaded' | 'error'
  selectedRouteIds: Set<string>
  /** Routes ridden by the active trip plan. While set, the map focuses on exactly these
   *  lines plus any Routes-panel selection: their full route lines stay drawn so the rider
   *  can board any vehicle of the route, and everything else hides. Set by
   *  setActiveItinerary, released by setMapFocus. */
  tripRouteIds: Set<string>
  selectedStationId: string | null
  selectedBusStopId: string | null
  /** The ferry landing whose sheet is open, for the map's highlight. Namespaced id. */
  selectedFerryLandingId: string | null
  /** The LIRR / Metro-North station whose sheet is open, for the map's highlight. */
  selectedRailStationId: string | null
  /** The NJ Transit bus stop whose sheet is open, for the map's highlight. Namespaced id. */
  selectedNjBusStopId: string | null
  /** A station / bus stop asked for from OUTSIDE the map — a favorite tapped in the
   *  Favorites sheet. MapPage owns the detail sheets in local state, so a component
   *  elsewhere drops the request here and MapPage opens and clears it. One-shot, unlike
   *  selected*Id above, which mirror what's already open. */
  pendingStationOpen: StationInfo | null
  pendingBusStopOpen: BusStopInfo | null
  wsConnected: boolean
  transitFilter: TransitFilter
  lastVehicleUpdateMs: number | null
  /**
   * Vehicles in service per route, EVERY fleet — from the server's VEHICLE_COUNTS, so it
   * covers fleets this client is not receiving (the socket only sends the modes it draws,
   * see services/websocket.ts). Null until the first counts arrive; read it through
   * `routeVehicleCounts`, which falls back to counting `vehicles`.
   */
  vehicleCounts: Record<string, number> | null

  upsertVehicles: (vehicles: VehiclePosition[]) => void
  upsertVehicle: (vehicle: VehiclePosition) => void
  setVehicleCounts: (counts: VehicleCounts) => void
  setAlerts: (alerts: ServiceAlert[]) => void
  /** Load upcoming alerts unless a load is running or the last is fresh. Safe to call on
   *  every open of the Alerts sheet. */
  loadUpcomingAlerts: () => void
  toggleSelectedRoute: (routeId: string) => void
  setSelectedRoutes: (routeIds: string[]) => void
  clearSelectedRoutes: () => void
  setTripRouteIds: (routeIds: string[]) => void
  setWsConnected: (connected: boolean) => void
  /** Replace the filter. An empty set is ignored, keeping the map from going blank. */
  setTransitFilter: (modes: Iterable<TransitMode>) => void
  /** Add or remove one mode. Removing the last one is a no-op, for the same reason. */
  toggleTransitMode: (mode: TransitMode) => void
  setSelectedStationId: (stationId: string | null) => void
  setSelectedBusStopId: (busStopId: string | null) => void
  setSelectedFerryLandingId: (landingId: string | null) => void
  setSelectedRailStationId: (stationId: string | null) => void
  setSelectedNjBusStopId: (stopId: string | null) => void
  requestStationOpen: (station: StationInfo) => void
  requestBusStopOpen: (stop: BusStopInfo) => void
  clearPendingOpen: () => void
  clearVehicles: () => void

  getVehiclesByRoute: (routeId: string) => VehiclePosition[]
  getVehicle: (vehicleId: string) => VehiclePosition | undefined
}

// A vehicle is dropped once it hasn't appeared in the feed for this long. The backend stops
// emitting a trip the moment it completes, so without this prune, finished trains linger
// forever, frozen at their last segment.
//
// It MUST comfortably exceed the worst realistic spacing between two SUBWAY snapshots, or a
// train merely between snapshots gets pruned — it vanishes, and the next snapshot's stale
// glide state snaps it forward: disappear→teleport→stutter. Real spacing isn't the nominal
// 15 s: a feed can drop a poll and a slow bus poll can delay the subway one, so 60–90 s gaps
// happen. 150 s rides those out. The only cost is a completed trip lingering a little
// longer, parked at its final stop — the glide clamps at progress 1, it can't creep past.
const VEHICLE_TTL_MS = 150_000

// Kept outside reactive state, so stamping them never triggers a re-render. Only the pruned
// `vehicles` map is reactive.
const vehicleLastSeenMs = new Map<string, number>()

// Each VEHICLE_COUNTS message replaces the fleets it names, so counts are kept per fleet and
// merged. A fleet whose counts stop coming (its poller down, or NJ Transit's empty small
// hours, which are not published) ages out on the vehicles' own TTL rather than claiming
// buses forever.
const countsByType = new Map<VehicleType, { atMs: number; counts: Record<string, number> }>()

/** Upcoming work changes when MTA republishes its schedule, not minute to minute. */
const UPCOMING_ALERTS_MAX_AGE_MS = 5 * 60_000
let upcomingAlertsLoadedAtMs = 0

function mergedCounts(now: number): Record<string, number> {
  const out: Record<string, number> = {}
  for (const [type, entry] of countsByType) {
    if (now - entry.atMs > VEHICLE_TTL_MS) {
      countsByType.delete(type)
      continue
    }
    for (const [route, n] of Object.entries(entry.counts)) out[route] = (out[route] ?? 0) + n
  }
  return out
}

function sameCounts(a: Record<string, number> | null, b: Record<string, number>): boolean {
  if (!a) return false
  const ka = Object.keys(a)
  return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k])
}

export const useTransitStore = createStore<TransitState>((set, get) => ({
  vehicles: {},
  alerts: [],
  upcomingAlerts: null,
  upcomingAlertsStatus: 'idle',
  selectedRouteIds: new Set<string>(),
  tripRouteIds: new Set<string>(),
  selectedStationId: null,
  selectedBusStopId: null,
  selectedFerryLandingId: null,
  selectedRailStationId: null,
  selectedNjBusStopId: null,
  pendingStationOpen: null,
  pendingBusStopOpen: null,
  wsConnected: false,
  transitFilter: ['subway'],
  lastVehicleUpdateMs: null,
  vehicleCounts: null,

  upsertVehicles: (vehicles) =>
    set((state) => {
      const now = Date.now()
      const next = { ...state.vehicles }
      for (const v of vehicles) {
        next[v.vehicleId] = v
        vehicleLastSeenMs.set(v.vehicleId, now)
      }
      // Each poll publishes a full snapshot of active vehicles, so anything not refreshed
      // within the TTL has completed its trip or left service.
      for (const id of Object.keys(next)) {
        if (now - (vehicleLastSeenMs.get(id) ?? 0) > VEHICLE_TTL_MS) {
          delete next[id]
          vehicleLastSeenMs.delete(id)
        }
      }
      return { vehicles: next, lastVehicleUpdateMs: now }
    }),

  setVehicleCounts: ({ types, counts }) =>
    set((state) => {
      const now = Date.now()
      // Each named fleet's counts are replaced whole: a route absent here has none running.
      // `counts` is one map across the named fleets (the connect snapshot names all five), so
      // it is filed under the first; the merge below sums fleets, so where it sits is moot.
      types.forEach((type, i) => countsByType.set(type, { atMs: now, counts: i === 0 ? { ...counts } : {} }))
      const merged = mergedCounts(now)
      // Unchanged counts are the common case between polls; keep the reference so a route
      // list doesn't re-render for nothing.
      return sameCounts(state.vehicleCounts, merged) ? {} : { vehicleCounts: merged }
    }),

  upsertVehicle: (vehicle) =>
    set((state) => {
      vehicleLastSeenMs.set(vehicle.vehicleId, Date.now())
      return {
        vehicles: { ...state.vehicles, [vehicle.vehicleId]: vehicle },
        lastVehicleUpdateMs: Date.now(),
      }
    }),

  // Filtered here as well as on the server: a server predating `alerts: 'live'`, or a
  // connect snapshot that beat this client's SUBSCRIBE, still sends the full set.
  setAlerts: (alerts) => set({ alerts: alerts.some((a) => a.upcoming) ? alerts.filter((a) => !a.upcoming) : alerts }),
  loadUpcomingAlerts: () => {
    const { upcomingAlertsStatus } = get()
    if (upcomingAlertsStatus === 'loading') return
    if (upcomingAlertsStatus === 'loaded' && Date.now() - upcomingAlertsLoadedAtMs < UPCOMING_ALERTS_MAX_AGE_MS) return
    set({ upcomingAlertsStatus: 'loading' })
    api.getUpcomingAlerts()
      .then((upcomingAlerts) => {
        upcomingAlertsLoadedAtMs = Date.now()
        set({ upcomingAlerts, upcomingAlertsStatus: 'loaded' })
      })
      // A stale list is better than none: keep what was loaded before, and say so only
      // when there is nothing to show.
      .catch(() => set((s) => ({ upcomingAlertsStatus: s.upcomingAlerts ? 'loaded' : 'error' })))
  },
  toggleSelectedRoute: (routeId) => set((state) => {
    const next = new Set(state.selectedRouteIds)
    if (next.has(routeId)) next.delete(routeId)
    else next.add(routeId)
    return { selectedRouteIds: next }
  }),
  setSelectedRoutes: (routeIds) => set({ selectedRouteIds: new Set(routeIds) }),
  clearSelectedRoutes: () => set({ selectedRouteIds: new Set<string>() }),
  setTripRouteIds: (routeIds) => set({ tripRouteIds: new Set(routeIds) }),
  setWsConnected: (connected) => set({ wsConnected: connected }),
  // Both only write on a real change: TransitMap diffs `transitFilter` by identity to decide
  // whether to rebuild the vehicle layers synchronously.
  setTransitFilter: (modes) => set((state) => {
    const next = normalizeFilter(modes)
    return next && !sameFilter(next, state.transitFilter) ? { transitFilter: next } : {}
  }),
  toggleTransitMode: (mode) => set((state) => {
    const cur = state.transitFilter
    const next = normalizeFilter(
      cur.includes(mode) ? cur.filter((m) => m !== mode) : [...cur, mode],
    )
    return next ? { transitFilter: next } : {}
  }),
  setSelectedStationId: (stationId) => set({ selectedStationId: stationId }),
  setSelectedBusStopId: (busStopId) => set({ selectedBusStopId: busStopId }),
  setSelectedFerryLandingId: (landingId) => set({ selectedFerryLandingId: landingId }),
  setSelectedRailStationId: (stationId) => set({ selectedRailStationId: stationId }),
  setSelectedNjBusStopId: (stopId) => set({ selectedNjBusStopId: stopId }),
  // Mutually exclusive (one tap opens one sheet), so each clears the other; clearPendingOpen
  // resets both once MapPage has consumed it.
  requestStationOpen: (station) => set({ pendingStationOpen: station, pendingBusStopOpen: null }),
  requestBusStopOpen: (stop) => set({ pendingBusStopOpen: stop, pendingStationOpen: null }),
  clearPendingOpen: () => set({ pendingStationOpen: null, pendingBusStopOpen: null }),
  clearVehicles: () => {
    vehicleLastSeenMs.clear()
    countsByType.clear()
    set({ vehicles: {}, vehicleCounts: null })
  },

  getVehiclesByRoute: (routeId) =>
    Object.values(get().vehicles).filter((v) => v.routeId === routeId),

  getVehicle: (vehicleId) => get().vehicles[vehicleId],
}))

/**
 * Vehicles in service per route, for "12 active" in a route list. The server's counts when it
 * sends them — they cover fleets this client is not receiving — else a count of the vehicles
 * the client has, which is what a server predating VEHICLE_COUNTS leaves (and such a server
 * still sends every vehicle, since it ignores `modes`).
 */
export function routeVehicleCounts(state: {
  vehicleCounts: Record<string, number> | null
  vehicles: Record<string, VehiclePosition>
}): Record<string, number> {
  if (state.vehicleCounts) return state.vehicleCounts
  const counts: Record<string, number> = {}
  for (const v of Object.values(state.vehicles)) counts[v.routeId] = (counts[v.routeId] ?? 0) + 1
  return counts
}

/** The routes the map should focus on. An active trip plan is exclusive — only its lines
 *  show, so a leftover Routes-panel selection can't draw unrelated lines beside the trip.
 *  With no trip, that selection applies. Empty set = no focus. */
export function focusedRouteIds(state: { selectedRouteIds: Set<string>; tripRouteIds: Set<string> }): Set<string> {
  return state.tripRouteIds.size > 0 ? state.tripRouteIds : state.selectedRouteIds
}
